import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import PolicyModal from './PolicyModal';
import { termsOfService, privacyPolicy } from './PolicyContent';

interface LoginViewProps {
  onInstallClick: () => void;
  canInstall: boolean;
}

const LoginView: React.FC<LoginViewProps> = ({ onInstallClick, canInstall }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, signup, error } = useAuth();
  
  const [isRegistering, setIsRegistering] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [policyModal, setPolicyModal] = useState<{ isOpen: boolean; title: string; content: string }>({ isOpen: false, title: '', content: '' });

  const videoRef = useRef<HTMLVideoElement>(null);
  const videoUrl = "https://videos.pexels.com/video-files/6084644/6084644-uhd_2560_1440_30fps.mp4";

  const passwordMismatch = isRegistering && password && passwordConfirm && password !== passwordConfirm;

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
        video.muted = true;
        video.play().catch(() => {});
    }
  }, [videoUrl]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await login(email, password);
    setIsSubmitting(false);
  };
  
  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== passwordConfirm) return;
    setIsSubmitting(true);
    await signup(email, password);
    setIsSubmitting(false);
  };

  const isSignupDisabled = !email || !password || !passwordConfirm || !agreedToTerms || !agreedToPolicy || password !== passwordConfirm || isSubmitting;

  const openPolicy = (type: 'terms' | 'privacy') => {
    setPolicyModal({
      isOpen: true,
      title: type === 'terms' ? '이용약관' : '개인정보처리방침',
      content: type === 'terms' ? termsOfService : privacyPolicy,
    });
  };

  return (
    <>
      <section className="relative h-screen flex flex-col justify-center items-center p-6 overflow-hidden bg-slate-900">
          <div className="absolute inset-0 w-full h-full bg-black z-0 pointer-events-none">
              <video 
                  key={videoUrl}
                  ref={videoRef}
                  className="absolute inset-0 w-full h-full object-cover"
                  src={videoUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
              />
              <div className="absolute inset-0 bg-black/20 z-10"></div>
          </div>
        
          <main className="w-full max-w-screen-2xl relative z-30 flex items-center justify-center">
             <div className="w-full max-w-[100rem] border-[5px] border-[#f43f5e] p-12 lg:p-40 flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-14">
                {/* Left Side: Text Content */}
                <div className="flex flex-col items-center lg:items-start text-center lg:text-left max-w-2xl w-full">
                    <h1 className="text-white text-4xl sm:text-5xl lg:text-8xl font-black tracking-tighter leading-[0.9] mb-6 drop-shadow-lg">
                        <span className="block mb-2">출제자를</span>
                        <span className="text-[#f43f5e]">정복하다.</span>
                    </h1>
                    
                    <div className="max-w-xl">
                        <p className="text-sm sm:text-base lg:text-xl text-white/90 leading-relaxed font-medium tracking-tight whitespace-normal lg:whitespace-nowrap drop-shadow-md">
                            PDF 속 잠자던 문제를{' '}
                            <b className="text-[#f43f5e] font-bold">살아있는 디지털 자산</b>으로.<br className="hidden sm:block" />
                            AI 기반의 문제 변형, 창작, 그리고 완벽한 학습지 제작까지.
                        </p>
                    </div>
                </div>

                {/* Right Side: Login Card */}
                <div className="w-full max-w-md relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-rose-400 to-orange-300 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition duration-1000"></div>
                    <div className="relative w-full bg-white/70 backdrop-blur-xl border border-white/60 shadow-2xl shadow-black/5 rounded-xl p-6 lg:p-8">
                        <h2 className="text-2xl lg:text-3xl font-bold text-center mb-2 text-slate-800">다정AI <span className="font-light text-rose-500">{isRegistering ? '회원가입' : '로그인'}</span></h2>
                        <p className="text-center text-slate-500 text-xs lg:text-sm mb-6 lg:mb-8 font-medium">선생님을 위한 최고의 파트너</p>
                        
                        {isRegistering ? (
                          <form onSubmit={handleSignupSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="email-signup" className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Email</label>
                                <input id="email-signup" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-white/80 border border-slate-200 rounded-lg py-2.5 px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" placeholder="name@example.com"/>
                            </div>
                            <div>
                                <label htmlFor="password-signup" className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Password (6자리 이상)</label>
                                <input id="password-signup" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-white/80 border border-slate-200 rounded-lg py-2.5 px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" placeholder="••••••••"/>
                            </div>
                             <div>
                                <label htmlFor="password-confirm" className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Password 확인</label>
                                <input id="password-confirm" type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} required className={`w-full bg-white/80 border rounded-lg py-2.5 px-4 text-slate-900 text-sm focus:outline-none focus:ring-2 transition-all ${passwordMismatch ? 'border-red-500 ring-red-500' : 'border-slate-200 focus:ring-rose-500'}`} placeholder="••••••••"/>
                                {passwordMismatch && <p className="text-red-500 text-xs mt-1">비밀번호가 일치하지 않습니다.</p>}
                            </div>

                            <div className="space-y-2 text-xs">
                                <label className="flex items-center gap-2 text-slate-600 cursor-pointer">
                                    <input type="checkbox" checked={agreedToTerms} onChange={() => setAgreedToTerms(p => !p)} className="h-4 w-4 accent-rose-500" />
                                    <span onClick={() => openPolicy('terms')} className="hover:text-rose-600 underline">이용약관</span>에 동의합니다. (필수)
                                </label>
                                <label className="flex items-center gap-2 text-slate-600 cursor-pointer">
                                    <input type="checkbox" checked={agreedToPolicy} onChange={() => setAgreedToPolicy(p => !p)} className="h-4 w-4 accent-rose-500" />
                                    <span onClick={() => openPolicy('privacy')} className="hover:text-rose-600 underline">개인정보처리방침</span>에 동의합니다. (필수)
                                </label>
                            </div>

                            {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded-lg text-rose-600 text-xs text-center font-bold">{error}</div>}
                            <button type="submit" disabled={isSignupDisabled} className="w-full bg-gradient-to-r from-rose-600 to-orange-500 hover:from-rose-500 hover:to-orange-400 text-white font-bold py-3 px-4 rounded-lg transition-all shadow-lg shadow-rose-500/20 disabled:opacity-50 disabled:cursor-not-allowed">
                                {isSubmitting ? '가입 중...' : '가입하기'}
                            </button>
                          </form>
                        ) : (
                          <form onSubmit={handleLoginSubmit} className="space-y-4 lg:space-y-5">
                            <div>
                                <label htmlFor="email" className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Email</label>
                                <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-white/80 border border-slate-200 rounded-lg py-2.5 lg:py-3 px-4 text-slate-900 text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all placeholder-slate-400" placeholder="name@example.com"/>
                            </div>
                            <div>
                                <label htmlFor="password" className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Password</label>
                                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-white/80 border border-slate-200 rounded-lg py-2.5 lg:py-3 px-4 text-slate-900 text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all placeholder-slate-400" placeholder="••••••••"/>
                            </div>
                            {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded-lg text-rose-600 text-xs text-center font-bold">{error}</div>}
                            <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-rose-600 to-orange-500 hover:from-rose-500 hover:to-orange-400 text-white font-bold py-3 lg:py-3.5 px-4 rounded-lg transition-all duration-300 shadow-lg shadow-rose-500/20 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 text-sm lg:text-base">
                                {isSubmitting ? '접속 중...' : '시작하기'}
                            </button>
                          </form>
                        )}
                        <div className="mt-4 pt-4 border-t border-slate-200 text-center">
                            <button onClick={() => { setIsRegistering(p => !p); setEmail(''); setPassword(''); setPasswordConfirm(''); }} className="text-xs text-slate-500 hover:text-rose-600 font-medium">
                                {isRegistering ? '이미 계정이 있으신가요? 로그인' : '계정이 없으신가요? 회원가입'}
                            </button>
                        </div>
                    </div>
                </div>
             </div>
          </main>
      </section>
      
      <PolicyModal 
        isOpen={policyModal.isOpen}
        onClose={() => setPolicyModal({ ...policyModal, isOpen: false })}
        title={policyModal.title}
        content={policyModal.content}
      />
    </>
  );
};

export default LoginView;